import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import { toast } from "sonner";

interface TOTPEnrollment {
  id: string;
  type: string;
  totp: {
    qr_code: string;
    secret: string;
    uri: string;
  };
}

export function useMFA() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [enrollmentData, setEnrollmentData] = useState<TOTPEnrollment | null>(null);

  // Get MFA factors
  const { data: factors, refetch: refetchFactors } = useQuery({
    queryKey: ["mfa-factors", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase.auth.mfa.listFactors();
      if (error) throw error;
      return data;
    },
    enabled: !!user?.id,
  });

  // Check if MFA is enabled (has verified TOTP factor)
  const mfaEnabled = factors?.totp?.some(f => f.status === "verified") ?? false;

  // Get current assurance level
  const getAAL = async () => {
    const { data, error } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
    if (error) throw error;
    return data;
  };

  // Enroll TOTP (start enrollment)
  const enrollTOTP = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.auth.mfa.enroll({
        factorType: "totp",
        friendlyName: "Authenticator App",
      });
      if (error) throw error;
      setEnrollmentData(data as TOTPEnrollment);
      return data;
    },
    onError: (error: Error) => {
      toast.error("Erro ao iniciar configuração 2FA: " + error.message);
    },
  });

  // Verify enrollment with code
  const verifyEnrollment = useMutation({
    mutationFn: async (code: string) => {
      if (!enrollmentData?.id) throw new Error("No enrollment in progress");

      const { data: challengeData, error: challengeError } = await supabase.auth.mfa.challenge({
        factorId: enrollmentData.id,
      });
      if (challengeError) throw challengeError;

      const { error: verifyError } = await supabase.auth.mfa.verify({
        factorId: enrollmentData.id,
        challengeId: challengeData.id,
        code,
      });
      if (verifyError) throw verifyError;

      // Update profile to reflect MFA is enabled
      await supabase.from("profiles").update({ mfa_enabled: true }).eq("user_id", user?.id);

      setEnrollmentData(null);
    },
    onSuccess: () => {
      refetchFactors();
      queryClient.invalidateQueries({ queryKey: ["profile"] });
      toast.success("2FA ativado com sucesso!");
    },
    onError: (error: Error) => {
      toast.error("Código inválido. Tente novamente.");
    },
  });

  // Unenroll (disable) MFA
  const unenrollFactor = useMutation({
    mutationFn: async (factorId: string) => {
      const { error } = await supabase.auth.mfa.unenroll({ factorId });
      if (error) throw error;

      // Update profile to reflect MFA is disabled
      await supabase.from("profiles").update({ mfa_enabled: false }).eq("user_id", user?.id);
    },
    onSuccess: () => {
      refetchFactors();
      queryClient.invalidateQueries({ queryKey: ["profile"] });
      toast.success("2FA desativado com sucesso!");
    },
    onError: (error: Error) => {
      toast.error("Erro ao desativar 2FA: " + error.message);
    },
  });

  // Create challenge for verification during login
  const createChallenge = async (factorId: string) => {
    const { data, error } = await supabase.auth.mfa.challenge({ factorId });
    if (error) throw error;
    return data;
  };

  // Verify challenge with code
  const verifyChallenge = async (factorId: string, challengeId: string, code: string) => {
    const { error } = await supabase.auth.mfa.verify({
      factorId,
      challengeId,
      code,
    });
    if (error) throw error;
  };

  return {
    mfaEnabled,
    factors,
    enrollmentData,
    enrollTOTP,
    verifyEnrollment,
    unenrollFactor,
    getAAL,
    createChallenge,
    verifyChallenge,
    refetchFactors,
  };
}
